import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';

const LoginModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login', 'register', or 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [forgotSent, setForgotSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password, remember_me: rememberMe }),
      });

      const data = await response.json();

      if (response.ok) {
        // ─── 2FA branch: admin accounts cannot complete login from this modal ───
        // The /api/auth/login response returns {requires_2fa: true, mfa_token, ...}
        // for admin accounts. The full TOTP + recovery flow lives on /admin-login.
        // Send the user there instead of pretending the login succeeded.
        if (data.requires_2fa) {
          try {
            localStorage.removeItem('user');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('bcs_session_token');
          } catch (_) { /* ignore */ }
          toast.info('This is an admin account — please sign in from the Admin Portal to complete 2FA.');
          resetForm();
          onClose();
          navigate('/admin-login');
          return;
        }

        // Store token in localStorage for persistent auth
        if (rememberMe && data.session_token) {
          localStorage.setItem('bcs_session_token', data.session_token);
        }
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('isAuthenticated', 'true');
        toast.success('Logged in successfully!');
        resetForm();
        onClose();
        if (data.user?.is_admin) {
          navigate('/admin');
        } else {
          navigate('/customer-portal');
        }
      } else {
        toast.error(data.detail || 'Login failed');
      }
    } catch (error) {
      toast.error('Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!email || !name || !company || !password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/auth/request-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, name, password, company, phone }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.status === 'exists') {
          toast.info(data.message);
          setMode('login');
        } else if (data.status === 'pending') {
          toast.info(data.message);
        } else {
          toast.success('Account request submitted! We\'ll email you when approved.');
          resetForm();
          onClose();
        }
      } else {
        toast.error(data.detail || 'Failed to submit request');
      }
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setCompany('');
    setPhone('');
    setShowPassword(false);
    setForgotSent(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email address'); return; }
    setIsSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setForgotSent(true);
        toast.success('Check your email for a reset link');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Something went wrong');
      }
    } catch { toast.error('Failed to send reset email'); }
    finally { setIsSubmitting(false); }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setForgotSent(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { resetForm(); onClose(); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_a4932d93-1a08-4aba-97f8-fc665534a903/artifacts/e6h6bxv7_BF726A6E-A369-4D1E-89C3-313F853560BC_1_105_c.jpeg" 
              alt="BCS Logo" 
              className="h-20 w-20 object-cover"
              style={{ clipPath: 'circle(37% at center)' }}
            />
          </div>
          <DialogTitle className="text-center text-2xl">
            Customer Portal
          </DialogTitle>
          <DialogDescription className="text-center">
            {mode === 'login' ? 'Sign in to access your portal' : mode === 'forgot' ? 'Enter your email to receive a reset link' : 'Request access to your BCS customer portal'}
          </DialogDescription>
        </DialogHeader>

        {/* Mode Toggle */}
        <div className="flex rounded-lg bg-slate-100 p-1 mb-4">
          <button
            type="button"
            onClick={() => switchMode('login')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              mode === 'login' 
                ? 'bg-white text-blue-900 shadow-sm' 
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => switchMode('register')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              mode === 'register' 
                ? 'bg-white text-blue-900 shadow-sm' 
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Request Access
          </button>
        </div>
        
        {mode === 'login' ? (
          /* Login Form */
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">Email Address</Label>
              <Input
                id="login-email"
                type="email"
                placeholder="john@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember-me"
                  data-testid="remember-me-checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-900 focus:ring-blue-900 cursor-pointer"
                />
                <Label htmlFor="remember-me" className="text-sm text-slate-600 cursor-pointer font-normal">
                  Remember me
                </Label>
              </div>
              <button type="button" onClick={() => switchMode('forgot')} className="text-sm text-blue-700 hover:text-blue-900 font-medium" data-testid="forgot-password-link">
                Forgot Password?
              </button>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-blue-900 hover:bg-blue-800 py-6"
              disabled={isSubmitting}
              data-testid="login-submit-button"
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        ) : mode === 'forgot' ? (
          /* Forgot Password Form */
          forgotSent ? (
            <div className="text-center py-4">
              <CheckCircle className="mx-auto mb-3 text-emerald-500" size={40} />
              <h3 className="font-semibold text-slate-900 mb-2">Check Your Email</h3>
              <p className="text-sm text-gray-500 mb-4">If an account exists for <strong>{email}</strong>, we've sent a password reset link.</p>
              <button type="button" onClick={() => switchMode('login')} className="text-sm text-blue-700 hover:text-blue-900 font-medium">
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email Address</Label>
                <Input id="forgot-email" type="email" placeholder="john@company.com" value={email}
                  onChange={e => setEmail(e.target.value)} required data-testid="forgot-email-input" />
              </div>
              <Button type="submit" className="w-full bg-blue-900 hover:bg-blue-800 py-6" disabled={isSubmitting} data-testid="forgot-submit-btn">
                {isSubmitting ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => switchMode('login')} className="text-sm text-blue-700 hover:text-blue-900 font-medium">
                  Back to Sign In
                </button>
              </div>
            </form>
          )
        ) : (
          /* Registration Form */
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                type="text"
                placeholder="John Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
              <Input
                id="email"
                type="email"
                placeholder="john@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="register-password">Password <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Input
                  id="register-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Minimum 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password <span className="text-red-500">*</span></Label>
              <Input
                id="confirm-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Company Name <span className="text-red-500">*</span></Label>
              <Input
                id="company"
                type="text"
                placeholder="ABC Industries"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <p className="text-sm text-gray-500">
              We'll review your request and email you when your access is approved.
            </p>
            
            <Button 
              type="submit" 
              className="w-full bg-blue-900 hover:bg-blue-800 py-6"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Request Account Access'}
            </Button>
          </form>
        )}
        
        <div className="text-center pt-2">
          <p className="text-sm text-gray-600">
            Need help? Call us at{' '}
            <a href="tel:+19493719948" className="text-blue-900 hover:underline font-medium">
              (949) 371-9948
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;
